import { JSONSnapshotData } from '../snapshot-data';
import type { BackgroundTask } from '../definitions/background-task';
import type { SnapshotStorage } from '../snapshot-storage';

// 触发自动保存的用户操作数量阈值
const AUTO_SAVE_OP_COUNT_THRESHOLD = 20;

// 自动保存的最大间隔时间，单位：毫秒
const AUTO_SAVE_MAX_INTERVAL = 3 * 60 * 1000;

enum AutoSaveStatus {
    Started = 'started',
    Saving = 'saving',
    Stopped = 'stopped',
}

export class AutoSaveTask implements BackgroundTask {
    public state: AutoSaveState;
    public status: AutoSaveStatus;

    private timer: ReturnType<typeof setTimeout>;

    constructor(
        private storage: SnapshotStorage,
        private logger: Pick<typeof console, 'debug' | 'error' | 'info' | 'log' | 'warn'>,
        private getData: () => unknown,
    ) {
        this.state = new AutoSaveState();
        this.status = AutoSaveStatus.Stopped;
        this.storage = storage;
    }

    public async perform(): Promise<void> {
        if (this.status === AutoSaveStatus.Saving) {
            return;
        }

        this.status = AutoSaveStatus.Saving;
        await this.storage.save(new JSONSnapshotData(this.getData()));
        this.reset();

        // 保存过程中可能被终止
        if (this.status === AutoSaveStatus.Saving) {
            this.status = AutoSaveStatus.Started;
        }
    }

    public start() {
        this.status = AutoSaveStatus.Started;
        this.loop();
    }

    public stop() {
        clearTimeout(this.timer);
        this.status = AutoSaveStatus.Stopped;
    }

    public async update(updater: (state: AutoSaveState) => AutoSaveState): Promise<void> {
        this.state = updater(this.state);

        if (this.needSave()) {
            await this.perform();
        }
    }

    private loop = () => {
        if (this.status === AutoSaveStatus.Stopped) {
            return;
        }

        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
            this.saveIfNeeded(this.loop);
        }, AUTO_SAVE_MAX_INTERVAL);
    };

    private needSave() {
        const { state, status } = this;
        if (status === AutoSaveStatus.Stopped || !state.opCount) {
            return false;
        }

        const isReachOpCountThreshold = state.opCount >= AUTO_SAVE_OP_COUNT_THRESHOLD;
        const isReachSaveTimeThreshold = Date.now() - state.lastSavedAt.getTime() >= AUTO_SAVE_MAX_INTERVAL;

        return isReachOpCountThreshold || isReachSaveTimeThreshold;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private saveIfNeeded = async (onFinish = () => {}) => {
        try {
            if (this.needSave()) await this.perform();
        } catch (error) {
            this.logger.error(error);
        }

        onFinish();
    };

    private reset() {
        this.state = this.state.reset();
    }
}

class AutoSaveState {
    opCount: number;
    lastSavedAt: Date;

    constructor() {
        this.opCount = 0;
        this.lastSavedAt = new Date();
    }

    clone() {
        return Object.assign(new AutoSaveState(), this);
    }

    increment(count: number) {
        const state = this.clone();
        state.opCount += count;
        return state;
    }

    reset() {
        const state = this.clone();
        state.opCount = 0;
        state.lastSavedAt = new Date();
        return state;
    }
}
